import { EXTERNAL_URLS } from '../../constants/url'
import {
  type ClientPortalCaseAlertItem,
  type ClientPortalCaseHistoryItem,
  type ClientPortalCompanyData,
  type ClientPortalInquiryTimeTrendItem,
  type ClientPortalOption,
  type ClientPortalProcessingTimeData,
  type ClientPortalProcessingTimeTrendItem,
  type ClientPortalVisaBulletinData,
  type ClientPortalVisaBulletinTrendItem,
} from '../../queries/case-dashboard/types'
import { type CaseApplicationType, type CaseLastStatus } from '../../queries/types'

export interface ApplicantCaseItem {
  caseId: number
  caseType: string
  caseNumber: string
  company: ClientPortalCompanyData
  applicationFiledDate: string
  applicationType: CaseApplicationType
  lastStatus: CaseLastStatus
  history: ClientPortalCaseHistoryItem[]
  isUscisCase: boolean
  processingTime: ClientPortalProcessingTimeData
  processingTimeTrend: ClientPortalProcessingTimeTrendItem[]
  inquiryDateTrend: ClientPortalInquiryTimeTrendItem[]
  visaBulletin: ClientPortalVisaBulletinData
  visaBulletinTrend: ClientPortalVisaBulletinTrendItem[]
  alertSchedule: ClientPortalCaseAlertItem[]
  priorityDate: string
  serviceCenter: string
  portalOption: ClientPortalOption
}

export const ALL_APPLICANT_CASES: ApplicantCaseItem[] = [
  {
    caseId: 1,
    caseType: 'I-130',
    caseNumber: 'ABC1234567890',
    company: {
      companyId: 1,
      logo: EXTERNAL_URLS.lawfullyLogo,
      name: 'Lawfully',
      email: 'lawfully@lawfully.ocm',
      phone: '000-000-0000',
    },
    applicationFiledDate: '2023-01-01',
    applicationType: 'uscis',
    lastStatus: {
      contents:
        'As of August 1, 2024, we are actively reviewing your Form I-130, Immigrant Petition for Alien Worker, Receipt Number ABC1234567890. Our records show nothing is outstanding at this time. We will let you know if we need anything from you. If you move, go to www.uscis.gov/addresschange to give us your new mailing address.',
      receivedDate: '2024-08-01',
      status: 'processing',
      title: 'Case Is Being Actively Reviewed By USCIS',
    },
    history: [
      {
        message:
          'As of August 1, 2024, we are actively reviewing your Form I-130, Immigrant Petition for Alien Worker, Receipt Number ABC1234567890. Our records show nothing is outstanding at this time. We will let you know if we need anything from you. If you move, go to www.uscis.gov/addresschange to give us your new mailing address.',
        receivedDate: '2024-08-01',
        title: 'Case Is Being Actively Reviewed By USCIS',
        type: 'status',
      },
      {
        message: 'We received your Form I-140, Immigrant Petition for Alien Worker, and sent you a receipt notice.',
        receivedDate: '2024-06-17 00:00:00',
        title: null,
        type: 'history',
      },
    ],
    isUscisCase: true,
    processingTime: {
      inquiryIcon: 1,
      inquiryText: 'Case is being processed normally.',
      inquirySubmitted: false,
      isPremium: false,
      processingTime: {
        estimatedTime: 14,
        isAvailableRequest: true,
        unit: 'Months',
      },
      visaBulletinSubmitted: false,
    },
    processingTimeTrend: [
      {
        estimatedTime: 23,
        timeUnit: 'Months',
        date: '2023-04-01',
      },
      {
        estimatedTime: 23.43,
        timeUnit: 'Months',
        date: '2023-05-01',
      },
      {
        estimatedTime: 23.08,
        timeUnit: 'Months',
        date: '2023-06-01',
      },
      {
        estimatedTime: 23,
        timeUnit: 'Months',
        date: '2023-07-01',
      },
      {
        estimatedTime: 23.17,
        timeUnit: 'Months',
        date: '2023-08-01',
      },
      {
        estimatedTime: 23.5,
        timeUnit: 'Months',
        date: '2023-09-01',
      },
      {
        estimatedTime: 26.5,
        timeUnit: 'Months',
        date: '2023-10-01',
      },
      {
        estimatedTime: 24.5,
        timeUnit: 'Months',
        date: '2023-11-01',
      },
      {
        estimatedTime: 23.25,
        timeUnit: 'Months',
        date: '2023-12-01',
      },
      {
        estimatedTime: 22.6,
        timeUnit: 'Months',
        date: '2024-01-01',
      },
      {
        estimatedTime: 22.5,
        timeUnit: 'Months',
        date: '2024-02-01',
      },
      {
        estimatedTime: 20,
        timeUnit: 'Months',
        date: '2024-03-01',
      },
      {
        estimatedTime: 19,
        timeUnit: 'Months',
        date: '2024-04-01',
      },
      {
        estimatedTime: 18,
        timeUnit: 'Months',
        date: '2024-05-01',
      },
      {
        estimatedTime: 17.25,
        timeUnit: 'Months',
        date: '2024-06-01',
      },
      {
        estimatedTime: 16,
        timeUnit: 'Months',
        date: '2024-07-01',
      },
      {
        estimatedTime: 15,
        timeUnit: 'Months',
        date: '2024-08-01',
      },
      {
        estimatedTime: 14,
        timeUnit: 'Months',
        date: '2024-09-01',
      },
      {
        estimatedTime: 13.83,
        timeUnit: 'Months',
        date: '2024-10-01',
      },
      {
        estimatedTime: 14,
        timeUnit: 'Months',
        date: '2024-11-01',
      },
      {
        estimatedTime: 14,
        timeUnit: 'Months',
        date: '2024-12-01',
      },
      {
        estimatedTime: 14,
        timeUnit: 'Months',
        date: '2025-01-01',
      },
      {
        estimatedTime: 14,
        timeUnit: 'Months',
        date: '2025-02-01',
      },
      {
        estimatedTime: 14.25,
        timeUnit: 'Months',
        date: '2025-03-01',
      },
    ],
    inquiryDateTrend: [
      {
        caseInquiryDate: '2020-08-31',
        date: '2023-04-01',
      },
      {
        caseInquiryDate: '2020-09-19',
        date: '2023-05-01',
      },
      {
        caseInquiryDate: '2020-12-21',
        date: '2023-06-01',
      },
      {
        caseInquiryDate: '2021-02-14',
        date: '2023-07-01',
      },
      {
        caseInquiryDate: '2021-03-07',
        date: '2023-08-01',
      },
      {
        caseInquiryDate: '2021-02-08',
        date: '2023-09-01',
      },
      {
        caseInquiryDate: '2020-10-26',
        date: '2023-10-01',
      },
      {
        caseInquiryDate: '2021-01-10',
        date: '2023-11-01',
      },
      {
        caseInquiryDate: '2021-05-08',
        date: '2023-12-01',
      },
      {
        caseInquiryDate: '2021-07-06',
        date: '2024-01-01',
      },
      {
        caseInquiryDate: '2021-08-03',
        date: '2024-02-01',
      },
      {
        caseInquiryDate: '2021-12-05',
        date: '2024-03-01',
      },
      {
        caseInquiryDate: '2022-02-21',
        date: '2024-04-01',
      },
      {
        caseInquiryDate: '2022-04-22',
        date: '2024-05-01',
      },
      {
        caseInquiryDate: '2022-06-23',
        date: '2024-06-01',
      },
      {
        caseInquiryDate: '2022-09-15',
        date: '2024-07-01',
      },
      {
        caseInquiryDate: '2023-02-21',
        date: '2024-08-01',
      },
      {
        caseInquiryDate: '2023-02-27',
        date: '2024-09-01',
      },
      {
        caseInquiryDate: '2023-06-11',
        date: '2024-10-01',
      },
      {
        caseInquiryDate: '2023-08-06',
        date: '2024-11-01',
      },
      {
        caseInquiryDate: '2023-09-10',
        date: '2024-12-01',
      },
      {
        caseInquiryDate: '2023-10-02',
        date: '2025-01-01',
      },
      {
        caseInquiryDate: '2023-10-22',
        date: '2025-02-01',
      },
      {
        caseInquiryDate: '2023-11-19',
        date: '2025-03-01',
      },
    ],
    visaBulletin: {
      bulletinDate: '2025-01-01',
      bulletinIcon: 1,
      bulletinText: 'You have filed Form I-485',
      filingDate: '2024-01-01',
      finalActionDate: '2024-03-01',
      isEligibleAOS: true,
      noticeText:
        'As of January 1, 2025, your priority date is current. You are eligible to file your Form I-485, Application to Register Permanent Residence or Adjust Status.',
      priorityDate: '2024-01-01',
      useField: '',
    },
    visaBulletinTrend: [
      {
        bulletinDate: '2025-04-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2025-03-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2025-02-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2025-01-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-12-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-11-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-10-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-09-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-08-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-07-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-06-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-05-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-04-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-03-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-02-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-01-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-12-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-11-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-10-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-09-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: '2023-08-01',
        finalActionDateType: 'Date',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-08-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: '2023-08-01',
        finalActionDateType: 'Date',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-07-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-06-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-05-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-04-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-03-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-02-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-01-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2022-12-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2022-11-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2022-10-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2022-09-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2022-08-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2022-07-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2022-06-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2022-05-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
    ],
    alertSchedule: [
      {
        alertType: 'RFE',
        deadline: '2025-02-15T00:00:00Z',
        notificationTime: '2025-02-14T00:00:00Z',
        timeUnit: 'day',
        timeAgo: 1,
      },
    ],
    priorityDate: '2024-01-01',
    serviceCenter: 'Texas Service Center',
    portalOption: {
      caseId: 1,
      processingTime: false,
      processingTimeTrend: false,
      inquiryDateTrend: false,
      visaBulletin: false,
      visaBulletinTrend: false,
      alertSchedule: false,
      isDefaultSetting: false,
    },
  },
  {
    caseId: 2,
    caseType: 'I-140',
    caseNumber: 'BCD1234567890',
    company: {
      companyId: 1,
      logo: EXTERNAL_URLS.lawfullyLogo,
      name: 'Lawfully',
      email: 'lawfully@lawfully.ocm',
      phone: '000-000-0000',
    },
    applicationFiledDate: '2023-02-01',
    applicationType: 'uscis',
    lastStatus: {
      contents:
        'As of January 1, 2024, we are actively reviewing your Form I-140, Immigrant Petition for Alien Worker, Receipt Number BCD1234567890. Our records show nothing is outstanding at this time. We will let you know if we need anything from you. If you move, go to www.uscis.gov/addresschange to give us your new mailing address.',
      receivedDate: '2024-01-01',
      status: 'processing',
      title: 'Case Is Being Actively Reviewed By USCIS',
    },
    history: [
      {
        message:
          'As of January 1, 2024, we are actively reviewing your Form I-140, Immigrant Petition for Alien Worker, Receipt Number BCD1234567890. Our records show nothing is outstanding at this time. We will let you know if we need anything from you. If you move, go to www.uscis.gov/addresschange to give us your new mailing address.',
        receivedDate: '2024-08-01',
        title: 'Case Is Being Actively Reviewed By USCIS',
        type: 'status',
      },
    ],
    isUscisCase: true,
    processingTime: {
      inquiryIcon: 2,
      inquiryText: 'Case inquiry is eligible.',
      inquirySubmitted: false,
      isPremium: false,
      processingTime: {
        estimatedTime: 10.5,
        isAvailableRequest: true,
        unit: 'Months',
      },
      visaBulletinSubmitted: false,
    },
    processingTimeTrend: [
      {
        estimatedTime: 23,
        timeUnit: 'Months',
        date: '2023-04-01',
      },
      {
        estimatedTime: 23.43,
        timeUnit: 'Months',
        date: '2023-05-01',
      },
      {
        estimatedTime: 23.08,
        timeUnit: 'Months',
        date: '2023-06-01',
      },
      {
        estimatedTime: 23,
        timeUnit: 'Months',
        date: '2023-07-01',
      },
      {
        estimatedTime: 23.17,
        timeUnit: 'Months',
        date: '2023-08-01',
      },
      {
        estimatedTime: 23.5,
        timeUnit: 'Months',
        date: '2023-09-01',
      },
      {
        estimatedTime: 26.5,
        timeUnit: 'Months',
        date: '2023-10-01',
      },
      {
        estimatedTime: 24.5,
        timeUnit: 'Months',
        date: '2023-11-01',
      },
      {
        estimatedTime: 23.25,
        timeUnit: 'Months',
        date: '2023-12-01',
      },
      {
        estimatedTime: 22.6,
        timeUnit: 'Months',
        date: '2024-01-01',
      },
      {
        estimatedTime: 22.5,
        timeUnit: 'Months',
        date: '2024-02-01',
      },
      {
        estimatedTime: 20,
        timeUnit: 'Months',
        date: '2024-03-01',
      },
      {
        estimatedTime: 19,
        timeUnit: 'Months',
        date: '2024-04-01',
      },
      {
        estimatedTime: 18,
        timeUnit: 'Months',
        date: '2024-05-01',
      },
      {
        estimatedTime: 17.25,
        timeUnit: 'Months',
        date: '2024-06-01',
      },
      {
        estimatedTime: 16,
        timeUnit: 'Months',
        date: '2024-07-01',
      },
      {
        estimatedTime: 15,
        timeUnit: 'Months',
        date: '2024-08-01',
      },
      {
        estimatedTime: 14,
        timeUnit: 'Months',
        date: '2024-09-01',
      },
      {
        estimatedTime: 13.83,
        timeUnit: 'Months',
        date: '2024-10-01',
      },
      {
        estimatedTime: 14,
        timeUnit: 'Months',
        date: '2024-11-01',
      },
      {
        estimatedTime: 14,
        timeUnit: 'Months',
        date: '2024-12-01',
      },
      {
        estimatedTime: 14,
        timeUnit: 'Months',
        date: '2025-01-01',
      },
      {
        estimatedTime: 14,
        timeUnit: 'Months',
        date: '2025-02-01',
      },
      {
        estimatedTime: 14.25,
        timeUnit: 'Months',
        date: '2025-03-01',
      },
    ],
    inquiryDateTrend: [
      {
        caseInquiryDate: '2020-08-31',
        date: '2023-04-01',
      },
      {
        caseInquiryDate: '2020-09-19',
        date: '2023-05-01',
      },
      {
        caseInquiryDate: '2020-12-21',
        date: '2023-06-01',
      },
      {
        caseInquiryDate: '2021-02-14',
        date: '2023-07-01',
      },
      {
        caseInquiryDate: '2021-03-07',
        date: '2023-08-01',
      },
      {
        caseInquiryDate: '2021-02-08',
        date: '2023-09-01',
      },
      {
        caseInquiryDate: '2020-10-26',
        date: '2023-10-01',
      },
      {
        caseInquiryDate: '2021-01-10',
        date: '2023-11-01',
      },
      {
        caseInquiryDate: '2021-05-08',
        date: '2023-12-01',
      },
      {
        caseInquiryDate: '2021-07-06',
        date: '2024-01-01',
      },
      {
        caseInquiryDate: '2021-08-03',
        date: '2024-02-01',
      },
      {
        caseInquiryDate: '2021-12-05',
        date: '2024-03-01',
      },
      {
        caseInquiryDate: '2022-02-21',
        date: '2024-04-01',
      },
      {
        caseInquiryDate: '2022-04-22',
        date: '2024-05-01',
      },
      {
        caseInquiryDate: '2022-06-23',
        date: '2024-06-01',
      },
      {
        caseInquiryDate: '2022-09-15',
        date: '2024-07-01',
      },
      {
        caseInquiryDate: '2023-02-21',
        date: '2024-08-01',
      },
      {
        caseInquiryDate: '2023-02-27',
        date: '2024-09-01',
      },
      {
        caseInquiryDate: '2023-06-11',
        date: '2024-10-01',
      },
      {
        caseInquiryDate: '2023-08-06',
        date: '2024-11-01',
      },
      {
        caseInquiryDate: '2023-09-10',
        date: '2024-12-01',
      },
      {
        caseInquiryDate: '2023-10-02',
        date: '2025-01-01',
      },
      {
        caseInquiryDate: '2023-10-22',
        date: '2025-02-01',
      },
      {
        caseInquiryDate: '2023-11-19',
        date: '2025-03-01',
      },
    ],
    visaBulletinTrend: [
      {
        bulletinDate: '2025-04-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2025-03-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2025-02-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2025-01-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-12-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-11-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-10-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-09-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-08-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-07-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-06-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-05-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-04-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-03-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-02-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-01-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-12-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-11-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-10-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-09-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: '2023-08-01',
        finalActionDateType: 'Date',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-08-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: '2023-08-01',
        finalActionDateType: 'Date',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-07-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-06-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-05-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-04-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-03-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-02-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-01-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2022-12-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2022-11-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2022-10-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2022-09-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2022-08-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2022-07-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2022-06-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2022-05-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
    ],
    alertSchedule: [
      {
        alertType: 'RFE',
        deadline: '2025-02-15T00:00:00Z',
        notificationTime: '2025-02-14T00:00:00Z',
        timeUnit: 'day',
        timeAgo: 1,
      },
    ],
    visaBulletin: {
      bulletinDate: '2025-01-01',
      bulletinIcon: 1,
      bulletinText: 'You have filed Form I-485',
      filingDate: '2024-01-01',
      finalActionDate: '2024-03-01',
      isEligibleAOS: true,
      noticeText:
        'As of January 1, 2025, your priority date is current. You are eligible to file your Form I-485, Application to Register Permanent Residence or Adjust Status.',
      priorityDate: '2024-01-01',
      useField: '',
    },
    priorityDate: '2023-06-01',
    serviceCenter: 'Texas Service Center',
    portalOption: {
      caseId: 2,
      processingTime: false,
      processingTimeTrend: false,
      inquiryDateTrend: false,
      visaBulletin: false,
      visaBulletinTrend: false,
      alertSchedule: false,
      isDefaultSetting: false,
    },
  },
  {
    caseId: 3,
    caseType: 'I-130',
    caseNumber: 'CDE1234567890',
    company: {
      companyId: 1,
      logo: EXTERNAL_URLS.lawfullyLogo,
      name: 'Lawfully',
      email: 'lawfully@lawfully.ocm',
      phone: '000-000-0000',
    },
    applicationFiledDate: '2023-03-01',
    applicationType: 'uscis',
    isUscisCase: true,
    lastStatus: {
      contents:
        'As of August 1, 2024, we are actively reviewing your Form I-130, Immigrant Petition for Alien Worker, Receipt Number CDE1234567890. Our records show nothing is outstanding at this time. We will let you know if we need anything from you. If you move, go to www.uscis.gov/addresschange to give us your new mailing address.',
      receivedDate: '2024-08-01',
      status: 'processing',
      title: 'Case Is Being Actively Reviewed By USCIS',
    },
    history: [
      {
        message:
          'As of August 1, 2024, we are actively reviewing your Form I-130, Immigrant Petition for Alien Worker, Receipt Number CDE1234567890. Our records show nothing is outstanding at this time. We will let you know if we need anything from you. If you move, go to www.uscis.gov/addresschange to give us your new mailing address.',
        receivedDate: '2024-08-01',
        title: 'Case Is Being Actively Reviewed By USCIS',
        type: 'status',
      },
      {
        message: 'We received your Form I-140, Immigrant Petition for Alien Worker, and sent you a receipt notice.',
        receivedDate: '2024-06-17 00:00:00',
        title: null,
        type: 'history',
      },
    ],
    processingTime: {
      inquiryIcon: 1,
      inquiryText: 'Case is being processed normally.',
      inquirySubmitted: false,
      isPremium: false,
      processingTime: {
        estimatedTime: 14,
        isAvailableRequest: true,
        unit: 'Months',
      },
      visaBulletinSubmitted: false,
    },
    processingTimeTrend: [
      {
        estimatedTime: 23,
        timeUnit: 'Months',
        date: '2023-04-01',
      },
      {
        estimatedTime: 23.43,
        timeUnit: 'Months',
        date: '2023-05-01',
      },
      {
        estimatedTime: 23.08,
        timeUnit: 'Months',
        date: '2023-06-01',
      },
      {
        estimatedTime: 23,
        timeUnit: 'Months',
        date: '2023-07-01',
      },
      {
        estimatedTime: 23.17,
        timeUnit: 'Months',
        date: '2023-08-01',
      },
      {
        estimatedTime: 23.5,
        timeUnit: 'Months',
        date: '2023-09-01',
      },
      {
        estimatedTime: 26.5,
        timeUnit: 'Months',
        date: '2023-10-01',
      },
      {
        estimatedTime: 24.5,
        timeUnit: 'Months',
        date: '2023-11-01',
      },
      {
        estimatedTime: 23.25,
        timeUnit: 'Months',
        date: '2023-12-01',
      },
      {
        estimatedTime: 22.6,
        timeUnit: 'Months',
        date: '2024-01-01',
      },
      {
        estimatedTime: 22.5,
        timeUnit: 'Months',
        date: '2024-02-01',
      },
      {
        estimatedTime: 20,
        timeUnit: 'Months',
        date: '2024-03-01',
      },
      {
        estimatedTime: 19,
        timeUnit: 'Months',
        date: '2024-04-01',
      },
      {
        estimatedTime: 18,
        timeUnit: 'Months',
        date: '2024-05-01',
      },
      {
        estimatedTime: 17.25,
        timeUnit: 'Months',
        date: '2024-06-01',
      },
      {
        estimatedTime: 16,
        timeUnit: 'Months',
        date: '2024-07-01',
      },
      {
        estimatedTime: 15,
        timeUnit: 'Months',
        date: '2024-08-01',
      },
      {
        estimatedTime: 14,
        timeUnit: 'Months',
        date: '2024-09-01',
      },
      {
        estimatedTime: 13.83,
        timeUnit: 'Months',
        date: '2024-10-01',
      },
      {
        estimatedTime: 14,
        timeUnit: 'Months',
        date: '2024-11-01',
      },
      {
        estimatedTime: 14,
        timeUnit: 'Months',
        date: '2024-12-01',
      },
      {
        estimatedTime: 14,
        timeUnit: 'Months',
        date: '2025-01-01',
      },
      {
        estimatedTime: 14,
        timeUnit: 'Months',
        date: '2025-02-01',
      },
      {
        estimatedTime: 14.25,
        timeUnit: 'Months',
        date: '2025-03-01',
      },
    ],
    inquiryDateTrend: [
      {
        caseInquiryDate: '2020-08-31',
        date: '2023-04-01',
      },
      {
        caseInquiryDate: '2020-09-19',
        date: '2023-05-01',
      },
      {
        caseInquiryDate: '2020-12-21',
        date: '2023-06-01',
      },
      {
        caseInquiryDate: '2021-02-14',
        date: '2023-07-01',
      },
      {
        caseInquiryDate: '2021-03-07',
        date: '2023-08-01',
      },
      {
        caseInquiryDate: '2021-02-08',
        date: '2023-09-01',
      },
      {
        caseInquiryDate: '2020-10-26',
        date: '2023-10-01',
      },
      {
        caseInquiryDate: '2021-01-10',
        date: '2023-11-01',
      },
      {
        caseInquiryDate: '2021-05-08',
        date: '2023-12-01',
      },
      {
        caseInquiryDate: '2021-07-06',
        date: '2024-01-01',
      },
      {
        caseInquiryDate: '2021-08-03',
        date: '2024-02-01',
      },
      {
        caseInquiryDate: '2021-12-05',
        date: '2024-03-01',
      },
      {
        caseInquiryDate: '2022-02-21',
        date: '2024-04-01',
      },
      {
        caseInquiryDate: '2022-04-22',
        date: '2024-05-01',
      },
      {
        caseInquiryDate: '2022-06-23',
        date: '2024-06-01',
      },
      {
        caseInquiryDate: '2022-09-15',
        date: '2024-07-01',
      },
      {
        caseInquiryDate: '2023-02-21',
        date: '2024-08-01',
      },
      {
        caseInquiryDate: '2023-02-27',
        date: '2024-09-01',
      },
      {
        caseInquiryDate: '2023-06-11',
        date: '2024-10-01',
      },
      {
        caseInquiryDate: '2023-08-06',
        date: '2024-11-01',
      },
      {
        caseInquiryDate: '2023-09-10',
        date: '2024-12-01',
      },
      {
        caseInquiryDate: '2023-10-02',
        date: '2025-01-01',
      },
      {
        caseInquiryDate: '2023-10-22',
        date: '2025-02-01',
      },
      {
        caseInquiryDate: '2023-11-19',
        date: '2025-03-01',
      },
    ],
    visaBulletin: {
      bulletinDate: '2025-01-01',
      bulletinIcon: 1,
      bulletinText: 'You have filed Form I-485',
      filingDate: '2024-01-01',
      finalActionDate: '2024-03-01',
      isEligibleAOS: true,
      noticeText:
        'As of January 1, 2025, your priority date is current. You are eligible to file your Form I-485, Application to Register Permanent Residence or Adjust Status.',
      priorityDate: '2024-01-01',
      useField: '',
    },
    visaBulletinTrend: [
      {
        bulletinDate: '2025-04-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2025-03-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2025-02-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2025-01-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-12-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-11-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-10-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-09-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-08-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-07-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-06-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-05-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-04-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-03-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-02-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-01-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-12-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-11-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-10-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-09-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: '2023-08-01',
        finalActionDateType: 'Date',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-08-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: '2023-08-01',
        finalActionDateType: 'Date',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-07-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-06-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-05-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-04-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-03-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-02-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-01-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2022-12-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2022-11-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2022-10-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2022-09-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2022-08-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2022-07-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2022-06-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2022-05-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
    ],
    alertSchedule: [
      {
        alertType: 'RFE',
        deadline: '2025-02-15T00:00:00Z',
        notificationTime: '2025-02-14T00:00:00Z',
        timeUnit: 'day',
        timeAgo: 1,
      },
    ],
    priorityDate: '2024-01-01',
    serviceCenter: 'Texas Service Center',
    portalOption: {
      caseId: 3,
      processingTime: false,
      processingTimeTrend: false,
      inquiryDateTrend: false,
      visaBulletin: false,
      visaBulletinTrend: false,
      alertSchedule: false,
      isDefaultSetting: false,
    },
  },
  {
    caseId: 4,
    caseType: 'I-140',
    caseNumber: 'DEF1234567890',
    company: {
      companyId: 1,
      logo: EXTERNAL_URLS.lawfullyLogo,
      name: 'Lawfully',
      email: 'lawfully@lawfully.ocm',
      phone: '000-000-0000',
    },
    applicationFiledDate: '2023-04-01',
    applicationType: 'uscis',
    lastStatus: {
      contents:
        'As of January 1, 2024, we are actively reviewing your Form I-140, Immigrant Petition for Alien Worker, Receipt Number DEF1234567890. Our records show nothing is outstanding at this time. We will let you know if we need anything from you. If you move, go to www.uscis.gov/addresschange to give us your new mailing address.',
      receivedDate: '2024-01-01',
      status: 'processing',
      title: 'Case Is Being Actively Reviewed By USCIS',
    },
    history: [
      {
        message:
          'As of January 1, 2024, we are actively reviewing your Form I-140, Immigrant Petition for Alien Worker, Receipt Number DEF1234567890. Our records show nothing is outstanding at this time. We will let you know if we need anything from you. If you move, go to www.uscis.gov/addresschange to give us your new mailing address.',
        receivedDate: '2024-08-01',
        title: 'Case Is Being Actively Reviewed By USCIS',
        type: 'status',
      },
    ],
    isUscisCase: true,
    processingTime: {
      inquiryIcon: 2,
      inquiryText: 'Case inquiry is eligible.',
      inquirySubmitted: false,
      isPremium: false,
      processingTime: {
        estimatedTime: 10.5,
        isAvailableRequest: true,
        unit: 'Months',
      },
      visaBulletinSubmitted: false,
    },
    processingTimeTrend: [
      {
        estimatedTime: 23,
        timeUnit: 'Months',
        date: '2023-04-01',
      },
      {
        estimatedTime: 23.43,
        timeUnit: 'Months',
        date: '2023-05-01',
      },
      {
        estimatedTime: 23.08,
        timeUnit: 'Months',
        date: '2023-06-01',
      },
      {
        estimatedTime: 23,
        timeUnit: 'Months',
        date: '2023-07-01',
      },
      {
        estimatedTime: 23.17,
        timeUnit: 'Months',
        date: '2023-08-01',
      },
      {
        estimatedTime: 23.5,
        timeUnit: 'Months',
        date: '2023-09-01',
      },
      {
        estimatedTime: 26.5,
        timeUnit: 'Months',
        date: '2023-10-01',
      },
      {
        estimatedTime: 24.5,
        timeUnit: 'Months',
        date: '2023-11-01',
      },
      {
        estimatedTime: 23.25,
        timeUnit: 'Months',
        date: '2023-12-01',
      },
      {
        estimatedTime: 22.6,
        timeUnit: 'Months',
        date: '2024-01-01',
      },
      {
        estimatedTime: 22.5,
        timeUnit: 'Months',
        date: '2024-02-01',
      },
      {
        estimatedTime: 20,
        timeUnit: 'Months',
        date: '2024-03-01',
      },
      {
        estimatedTime: 19,
        timeUnit: 'Months',
        date: '2024-04-01',
      },
      {
        estimatedTime: 18,
        timeUnit: 'Months',
        date: '2024-05-01',
      },
      {
        estimatedTime: 17.25,
        timeUnit: 'Months',
        date: '2024-06-01',
      },
      {
        estimatedTime: 16,
        timeUnit: 'Months',
        date: '2024-07-01',
      },
      {
        estimatedTime: 15,
        timeUnit: 'Months',
        date: '2024-08-01',
      },
      {
        estimatedTime: 14,
        timeUnit: 'Months',
        date: '2024-09-01',
      },
      {
        estimatedTime: 13.83,
        timeUnit: 'Months',
        date: '2024-10-01',
      },
      {
        estimatedTime: 14,
        timeUnit: 'Months',
        date: '2024-11-01',
      },
      {
        estimatedTime: 14,
        timeUnit: 'Months',
        date: '2024-12-01',
      },
      {
        estimatedTime: 14,
        timeUnit: 'Months',
        date: '2025-01-01',
      },
      {
        estimatedTime: 14,
        timeUnit: 'Months',
        date: '2025-02-01',
      },
      {
        estimatedTime: 14.25,
        timeUnit: 'Months',
        date: '2025-03-01',
      },
    ],
    inquiryDateTrend: [
      {
        caseInquiryDate: '2020-08-31',
        date: '2023-04-01',
      },
      {
        caseInquiryDate: '2020-09-19',
        date: '2023-05-01',
      },
      {
        caseInquiryDate: '2020-12-21',
        date: '2023-06-01',
      },
      {
        caseInquiryDate: '2021-02-14',
        date: '2023-07-01',
      },
      {
        caseInquiryDate: '2021-03-07',
        date: '2023-08-01',
      },
      {
        caseInquiryDate: '2021-02-08',
        date: '2023-09-01',
      },
      {
        caseInquiryDate: '2020-10-26',
        date: '2023-10-01',
      },
      {
        caseInquiryDate: '2021-01-10',
        date: '2023-11-01',
      },
      {
        caseInquiryDate: '2021-05-08',
        date: '2023-12-01',
      },
      {
        caseInquiryDate: '2021-07-06',
        date: '2024-01-01',
      },
      {
        caseInquiryDate: '2021-08-03',
        date: '2024-02-01',
      },
      {
        caseInquiryDate: '2021-12-05',
        date: '2024-03-01',
      },
      {
        caseInquiryDate: '2022-02-21',
        date: '2024-04-01',
      },
      {
        caseInquiryDate: '2022-04-22',
        date: '2024-05-01',
      },
      {
        caseInquiryDate: '2022-06-23',
        date: '2024-06-01',
      },
      {
        caseInquiryDate: '2022-09-15',
        date: '2024-07-01',
      },
      {
        caseInquiryDate: '2023-02-21',
        date: '2024-08-01',
      },
      {
        caseInquiryDate: '2023-02-27',
        date: '2024-09-01',
      },
      {
        caseInquiryDate: '2023-06-11',
        date: '2024-10-01',
      },
      {
        caseInquiryDate: '2023-08-06',
        date: '2024-11-01',
      },
      {
        caseInquiryDate: '2023-09-10',
        date: '2024-12-01',
      },
      {
        caseInquiryDate: '2023-10-02',
        date: '2025-01-01',
      },
      {
        caseInquiryDate: '2023-10-22',
        date: '2025-02-01',
      },
      {
        caseInquiryDate: '2023-11-19',
        date: '2025-03-01',
      },
    ],
    visaBulletin: {
      bulletinDate: '2025-01-01',
      bulletinIcon: 1,
      bulletinText: 'You have filed Form I-485',
      filingDate: '2024-01-01',
      finalActionDate: '2024-03-01',
      isEligibleAOS: true,
      noticeText:
        'As of January 1, 2025, your priority date is current. You are eligible to file your Form I-485, Application to Register Permanent Residence or Adjust Status.',
      priorityDate: '2024-01-01',
      useField: '',
    },
    visaBulletinTrend: [
      {
        bulletinDate: '2025-04-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2025-03-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2025-02-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2025-01-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-12-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-11-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-10-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-09-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-08-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-07-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-06-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-05-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-04-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-03-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-02-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2024-01-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-12-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-11-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-10-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-09-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: '2023-08-01',
        finalActionDateType: 'Date',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-08-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: '2023-08-01',
        finalActionDateType: 'Date',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-07-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-06-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-05-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-04-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-03-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-02-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2023-01-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2022-12-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2022-11-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2022-10-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2022-09-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2022-08-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2022-07-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2022-06-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
      {
        bulletinDate: '2022-05-01',
        filingDate: null,
        filingDateType: 'C',
        finalActionDate: null,
        finalActionDateType: 'C',
        lastMonthActionDateDiff: 0,
        lastMonthFilingDateDiff: 0,
      },
    ],
    alertSchedule: [
      {
        alertType: 'RFE',
        deadline: '2025-02-15T00:00:00Z',
        notificationTime: '2025-02-14T00:00:00Z',
        timeUnit: 'day',
        timeAgo: 1,
      },
    ],
    priorityDate: '2023-06-01',
    serviceCenter: 'Texas Service Center',
    portalOption: {
      caseId: 4,
      processingTime: false,
      processingTimeTrend: false,
      inquiryDateTrend: false,
      visaBulletin: false,
      visaBulletinTrend: false,
      alertSchedule: false,
      isDefaultSetting: false,
    },
  },
]
